<template>
  <div class="live-tracker-availability container is-full">
    <b-tabs v-model="activeTab" :animated="false">
      <b-tab-item
        v-if="liveSpotTrackerAvailabilities.length > 0"
        label="Balises SPOT"
        :value="spotTrackerType"
      >
        <live-tracker-availability-checklist
          :loading="loading"
          :trackers="spotTrackers"
          :vehicleNumbers="vehicleNumbers"
          :trackerAvailabilities="liveSpotTrackerAvailabilities"
          @loading="loading = $event"
          @updated="load"
          @updateTrackers="updateLiveSpotTrackers"
          @updateTrackerAvailabilities="updateLiveSpotTrackerAvailabilities"
        />
      </b-tab-item>
      <b-tab-item
        v-if="liveQueclinkTrackerAvailabilities.length > 0"
        label="Balises QUECLINK"
        :value="queclinkTrackerType"
      >
        <live-tracker-availability-checklist
          :loading="loading"
          :trackers="queclinkTrackers"
          :vehicleNumbers="vehicleNumbers"
          :trackerAvailabilities="liveQueclinkTrackerAvailabilities"
          @loading="loading = $event"
          @updated="load"
          @updateTrackers="updateLiveQueclinkTrackers"
          @updateTrackerAvailabilities="updateLiveQueclinkTrackerAvailabilities"
        />
      </b-tab-item>
      <live-tracker-availability-table
        v-if="
          liveQueclinkTrackerAvailabilities.length === 0 &&
          liveSpotTrackerAvailabilities.length === 0
        "
        :loading="loading"
        :trackers="[]"
        :checked-trackers="[]"
        class="column is-full"
      />
    </b-tabs>
    <b-field v-if="enabledSubmitButton" class="column is-full-mobile">
      <b-button type="is-success" :loading="loading" @click="submit">
        Confirmer la réception
      </b-button>
    </b-field>
  </div>
</template>

<script>
import LiveTrackerAvailabilityTable from './LiveTrackerAvailabilityTable.vue'
import LiveTrackerAvailabilityChecklist from './LiveTrackerAvailabilityChecklist.vue'
import { AVAILABILITY_UNAVAILABLE } from '@constants/tracker/availability.js'
import { QUECLINK_TRACKER_TYPE, SPOT_TRACKER_TYPE } from '@constants/tracker/type'

export default {
  name: 'LiveTrackerAvailability',

  components: {
    LiveTrackerAvailabilityTable,
    LiveTrackerAvailabilityChecklist,
  },

  props: {
    live: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      activeTab: SPOT_TRACKER_TYPE,
      loading: false,
      liveVehicles: [],
      spotTrackers: [],
      queclinkTrackers: [],
      liveSpotTrackerAvailabilities: [],
      liveQueclinkTrackerAvailabilities: [],
    }
  },

  created() {
    this.spotTrackerType = SPOT_TRACKER_TYPE
    this.queclinkTrackerType = QUECLINK_TRACKER_TYPE
  },

  async mounted() {
    await this.load()

    this.selectDefaultTab()
  },

  computed: {
    vehicleNumbers() {
      return this.liveVehicles.reduce(
        (vehicles, vehicle) => ({
          ...vehicles,
          ...(vehicle.spotTrackerId ? { [vehicle.spotTrackerId]: vehicle.number } : {}),
          ...(vehicle.queclinkTrackerId ? { [vehicle.queclinkTrackerId]: vehicle.number } : {}),
        }),
        {},
      )
    },

    liveTrackerAvailabilitiesNotAvailable() {
      return [
        ...this.liveSpotTrackerAvailabilities,
        ...this.liveQueclinkTrackerAvailabilities,
      ].filter(
        (liveTrackerAvailability) =>
          liveTrackerAvailability.availability === AVAILABILITY_UNAVAILABLE,
      )
    },

    enabledSubmitButton() {
      const lockedTrackerAvailabilities = this.liveTrackerAvailabilitiesNotAvailable.filter(
        (liveTrackerAvailability) => !liveTrackerAvailability.locked,
      )

      return (
        lockedTrackerAvailabilities.length === 0 &&
        (this.liveSpotTrackerAvailabilities.length > 0 ||
          this.liveQueclinkTrackerAvailabilities.length > 0)
      )
    },
  },

  methods: {
    async load() {
      this.loading = true

      const [
        spotTrackers,
        queclinkTrackers,
        liveVehicles,
        liveSpotTrackerAvailabilities,
        liveQueclinkTrackerAvailabilities,
        reassignedLiveSpotTrackerAvailabilities,
        reassignedLiveQueclinkTrackerAvailabilities,
      ] = await Promise.all([
        this.fetchSpotTrackers(),
        this.fetchQueclinkTrackers(),
        this.fetchLiveVehicles(this.live.id),
        this.fetchLiveSpotTrackerAvailabilities(),
        this.fetchLiveQueclinkTrackerAvailabilities(),
        this.fetchReassignedLiveSpotTrackerAvailabilities(),
        this.fetchReassignedLiveQueclinkTrackerAvailabilities(),
      ])

      this.spotTrackers = spotTrackers
      this.queclinkTrackers = queclinkTrackers
      this.liveVehicles = liveVehicles
      this.liveSpotTrackerAvailabilities = this.formatTrackerAvailabilities(
        liveSpotTrackerAvailabilities,
        reassignedLiveSpotTrackerAvailabilities,
        spotTrackers,
      )
      this.liveQueclinkTrackerAvailabilities = this.formatTrackerAvailabilities(
        liveQueclinkTrackerAvailabilities,
        reassignedLiveQueclinkTrackerAvailabilities,
        queclinkTrackers,
      )

      this.loading = false
    },

    selectDefaultTab() {
      if (
        this.liveSpotTrackerAvailabilities.length === 0 &&
        this.liveQueclinkTrackerAvailabilities.length > 0
      ) {
        this.activeTab = QUECLINK_TRACKER_TYPE
      }
    },

    formatTrackerAvailabilities(availabilities, reassignedAvailabilities, trackers) {
      const reassignedIds = new Set(reassignedAvailabilities.map(({ id }) => id))
      const trackerAvailability = new Map(
        trackers.map(({ id, availability }) => [id, availability]),
      )

      return availabilities.map((availability) => {
        const trackerId = availability.spotTrackerId ?? availability.queclinkTrackerId
        const reassigned = reassignedIds.has(availability.id)
        const desynchronized =
          !reassigned && trackerAvailability.get(trackerId) !== availability.availability

        return { ...availability, trackerId, desynchronized, locked: reassigned || desynchronized }
      })
    },

    async fetchLiveSpotTrackerAvailabilities() {
      return await this.$services.liveSpotTrackerAvailabilityService.getAll(this.live.id)
    },

    async fetchLiveQueclinkTrackerAvailabilities() {
      return await this.$services.liveQueclinkTrackerAvailabilityService.getAll(this.live.id)
    },

    async fetchReassignedLiveSpotTrackerAvailabilities() {
      return await this.$services.liveSpotTrackerAvailabilityService.getAll(this.live.id, {
        reassigned: true,
      })
    },

    async fetchReassignedLiveQueclinkTrackerAvailabilities() {
      return await this.$services.liveQueclinkTrackerAvailabilityService.getAll(this.live.id, {
        reassigned: true,
      })
    },

    async fetchLiveVehicles(liveId) {
      return await this.$services.liveVehicleService.getAll(liveId)
    },

    async fetchSpotTrackers() {
      return await this.$services.spotTrackerService.getAllByLiveId(this.live.id)
    },

    async fetchQueclinkTrackers() {
      return await this.$services.queclinkTrackerService.getAllByLiveId(this.live.id)
    },

    async updateLiveSpotTrackers({ resolve, reject, trackers }) {
      try {
        await this.$services.spotTrackerService.updateAll({ spotTrackers: trackers })

        resolve()
      } catch (err) {
        reject(err)
      }
    },

    async updateLiveQueclinkTrackers({ resolve, reject, trackers }) {
      try {
        await this.$services.queclinkTrackerService.updateAll({ queclinkTrackers: trackers })

        resolve()
      } catch (err) {
        reject(err)
      }
    },

    async updateLiveSpotTrackerAvailabilities({ resolve, reject, trackerAvailabilities }) {
      try {
        await this.$services.liveSpotTrackerAvailabilityService.updateAll({
          liveId: this.live.id,
          liveSpotTrackerAvailabilities: trackerAvailabilities,
        })

        resolve()
      } catch (err) {
        reject(err)
      }
    },

    async updateLiveQueclinkTrackerAvailabilities({ resolve, reject, trackerAvailabilities }) {
      try {
        await this.$services.liveQueclinkTrackerAvailabilityService.updateAll({
          liveId: this.live.id,
          liveQueclinkTrackerAvailabilities: trackerAvailabilities,
        })

        resolve()
      } catch (err) {
        reject(err)
      }
    },

    submit() {
      if (this.enabledSubmitButton) {
        this.$emit('submit')
      }
    },
  },
}
</script>

<style lang="scss">
@import 'variables';

.live-tracker-availability {
  .tab-content {
    background-color: $color-secondary-lightest;
    border-radius: 0 0 $border-radius-s $border-radius-s;
  }
}
</style>
